// Widget types
export type WidgetType =
  | 'weather'
  | 'calendar'
  | 'clock'
  | 'drive-time'
  | 'kasa'
  | 'wol'
  | 'brightness'
  | 'sun-moon'
  | 'aqi'
  | 'uv'
  | 'iframe'
  | 'climate'
  | 'timer'
  | 'ptt'
  | 'mqtt'
  | 'adguard'
  | 'notifications'
  | 'schedule'
  | 'iframe-popup'
  | 'system-stats'
  | 'money'
  | 'battery-status'
  | 'private-mode'
  | 'planes';

// Anchor positions for pixel-based positioning
export type AnchorPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

// Panel configuration
export interface PanelConfig {
  id: string;
  widget: WidgetType;
  // Percentage-based positioning (0-100) - used for background/scaling widgets
  x: number;
  y: number;
  width: number;
  height: number;
  // Pixel-based anchored positioning - used for floating widgets that need consistent sizing
  // When anchor is set, these pixel values take precedence over percentages
  anchor?: AnchorPosition;
  offsetX?: number; // pixels from anchor edge
  offsetY?: number; // pixels from anchor edge
  widthPx?: number; // fixed pixel width
  heightPx?: number; // fixed pixel height
  refresh?: string; // Duration string like "5m", "1h"
  args?: Record<string, unknown>;
  // Visible/usable in front of the blur while Private Mode is active (default: hidden)
  public?: boolean;
}

// Default settings per widget type (used when adding new widgets)
// Frameless widgets get anchor defaults for consistent pixel-based sizing
export const WIDGET_DEFAULTS: Record<WidgetType, Partial<PanelConfig>> = {
  weather: { width: 20, height: 25, refresh: '30m' },
  calendar: { width: 30, height: 40, refresh: '5m' },
  clock: {
    width: 10,
    height: 6,
    anchor: 'top-right',
    widthPx: 120,
    heightPx: 51,
    offsetX: 16,
    offsetY: 46,
  },
  'drive-time': {
    width: 6,
    height: 6,
    refresh: '5m',
    anchor: 'bottom-left',
    widthPx: 56,
    heightPx: 56,
    offsetX: 16,
    offsetY: 16,
  },
  kasa: {
    width: 6,
    height: 6,
    refresh: '5m',
    anchor: 'bottom-right',
    widthPx: 56,
    heightPx: 56,
    offsetX: 80,
    offsetY: 16,
  },
  wol: {
    width: 6,
    height: 6,
    refresh: '5m',
    anchor: 'bottom-right',
    widthPx: 56,
    heightPx: 56,
    offsetX: 144,
    offsetY: 16,
  },
  brightness: {
    width: 6,
    height: 6,
    refresh: '1m',
    anchor: 'bottom-right',
    widthPx: 56,
    heightPx: 56,
    offsetX: 208,
    offsetY: 16,
  },
  'sun-moon': { width: 15, height: 15, refresh: '1h' },
  aqi: { width: 15, height: 15, refresh: '30m' },
  uv: { width: 15, height: 15, refresh: '30m' },
  planes: { width: 20, height: 20, refresh: '1m' },
  iframe: { width: 40, height: 40 },
  climate: {
    width: 12,
    height: 12,
    refresh: '1m',
    anchor: 'top-right',
    widthPx: 130,
    heightPx: 104,
    offsetX: 16,
    offsetY: 16,
  },
  timer: {
    width: 6,
    height: 6,
    anchor: 'bottom-right',
    widthPx: 56,
    heightPx: 56,
    offsetX: 16,
    offsetY: 80,
  },
  ptt: {
    width: 6,
    height: 6,
    anchor: 'bottom-right',
    widthPx: 56,
    heightPx: 56,
    offsetX: 16,
    offsetY: 144,
  },
  mqtt: { width: 10, height: 10, refresh: '10s' },
  adguard: {
    width: 6,
    height: 6,
    refresh: '10s',
    anchor: 'bottom-right',
    widthPx: 56,
    heightPx: 56,
    offsetX: 272,
    offsetY: 16,
  },
  notifications: {
    width: 6,
    height: 6,
    anchor: 'bottom-right',
    widthPx: 56,
    heightPx: 56,
    offsetX: 16,
    offsetY: 16,
  },
  schedule: { width: 25, height: 50, refresh: '5m' },
  'iframe-popup': {
    width: 8,
    height: 10,
    anchor: 'bottom-left',
    widthPx: 72,
    heightPx: 80,
    offsetX: 80,
    offsetY: 16,
  },
  'system-stats': {
    width: 6,
    height: 6,
    refresh: '10s',
    anchor: 'top-left',
    widthPx: 56,
    heightPx: 56,
    offsetX: 16,
    offsetY: 16,
  },
  money: { width: 25, height: 20, refresh: '15m' },
  'battery-status': {
    width: 6,
    height: 6,
    refresh: '5m',
    anchor: 'bottom-right',
    widthPx: 56,
    heightPx: 56,
    offsetX: 336,
    offsetY: 16,
  },
  'private-mode': {
    width: 6,
    height: 6,
    anchor: 'bottom-right',
    widthPx: 56,
    heightPx: 56,
    offsetX: 400,
    offsetY: 16,
  },
};

// Screen configuration
export interface ScreenConfig {
  id: string;
  name: string;
  panels: PanelConfig[];
}

// Drive time route configuration
export interface DriveTimeRoute {
  origin: string;
  destination: string;
  via?: string[];
  viaLabel?: string;
  days: string[];
  startTime: string;
  endTime: string;
  label?: string;
  minTimeToShow?: number;
}

// Drive time configuration
export interface DriveTimeConfig {
  locations: Record<string, string>;
  routes: DriveTimeRoute[];
}

// Calendar configuration
export interface CalendarConfig {
  hidden?: string[];
  names?: Record<string, string>;
  headerHeight?: number; // Extra height in pixels for header section (0-200)
  showMonthPrefix?: boolean; // Show 3-char month prefix on each date (e.g. "Jun 1")
}

// Location configuration (for weather widgets)
export interface LocationConfig {
  lat: number;
  lon: number;
  city?: string;
  state?: string;
  query?: string;
}

// Brightness configuration (for auto-brightness on kiosk)
export interface BrightnessConfig {
  enabled?: boolean;
  lat?: number;
  lon?: number;
  city?: string;
  state?: string;
  locationName?: string;
  dayBrightness?: number;
  nightBrightness?: number;
  transitionMins?: number;
}

// Climate sensor configuration
export interface ClimateConfig {
  indoor?: string; // Zigbee2MQTT device friendly_name
  outdoor?: string;
  unit?: 'C' | 'F'; // Temperature unit (default: C)
}

// Theme mode for global settings
export type ThemeMode = 'dark' | 'light' | 'system';

// Wake word options (built-in, no training needed)
export type WakeWord = 'hey_jarvis' | 'alexa' | 'hey_mycroft' | 'hey_rhasspy';

// Vosk speech model options
export type VoskModel = 'small' | 'medium' | 'large';

// Vosk model metadata (returned from backend)
export interface VoskModelInfo {
  id: VoskModel;
  name: string;
  size: string;
  description: string;
  downloaded: boolean;
  downloading?: boolean;
  progress?: number; // 0-100 during download
}

// Piper TTS voice options
export type TtsVoice = 'amy' | 'danny' | 'lessac' | 'ryan';

// Voice response mode - how to show command responses
export type VoiceResponseMode = 'tts' | 'modal' | 'both' | 'none';

// Piper voice metadata (returned from backend)
export interface TtsVoiceInfo {
  id: TtsVoice;
  name: string;
  description: string;
  size: string;
  downloaded: boolean;
  downloading?: boolean;
  progress?: number; // 0-100 during download
}

// Global settings
export interface GlobalSettings {
  theme: ThemeMode;
  defaultLocation?: LocationConfig;
  hideCursor: boolean;
  relayUrl?: string;
  zigbeeUrl?: string; // Zigbee2MQTT UI URL
  internalApiKey?: string; // Shared secret for CF function endpoints
  // Voice control
  voiceEnabled?: boolean;
  wakeWord?: WakeWord;
  voiceModel?: VoskModel;
  ttsVoice?: TtsVoice;
  voiceResponseMode?: VoiceResponseMode;
  maxRecordingDuration?: number; // seconds, for PTT and wake word
}

// Dashboard configuration
export interface DashboardConfig {
  screens: ScreenConfig[];
  activeScreenIndex: number;
  dark: boolean;
  driveTime?: DriveTimeConfig;
  calendar?: CalendarConfig;
  climate?: ClimateConfig;
  locations?: Record<string, LocationConfig>;
  defaultLocation?: LocationConfig;
  brightness?: BrightnessConfig;
  globalSettings?: GlobalSettings;
  // Generic storage for any widget - third-party widgets can persist config here
  // Usage: widgetData['my-widget-id'] = { ...any config... }
  widgetData?: Record<string, unknown>;
}

// Default dashboard configuration (minimal fallback - actual defaults come from public/config/dashboard.json)
export const DEFAULT_CONFIG: DashboardConfig = {
  screens: [{ id: 'screen-1', name: 'Main', panels: [] }],
  activeScreenIndex: 0,
  dark: true,
};

// Duration parsing utility type
export type DurationUnit = 's' | 'm' | 'h' | 'd';

// Parse duration string to milliseconds
export function parseDuration(duration?: string): number | null {
  if (!duration) return null;
  const match = duration.match(/^(\d+)(s|m|h|d)$/);
  if (!match) return null;

  const value = parseInt(match[1], 10);
  const unit = match[2] as DurationUnit;

  const multipliers: Record<DurationUnit, number> = {
    s: 1000,
    m: 60 * 1000,
    h: 60 * 60 * 1000,
    d: 24 * 60 * 60 * 1000,
  };

  return value * multipliers[unit];
}

// Generate unique ID
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
